import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getConfigSons } from '../../services/SonsService';
import { resolverUrlSom } from '../../utils/sonsPresets';

const AudioContext = createContext();
export const useGlobalAudio = () => useContext(AudioContext);

export const GlobalAudioProvider = ({ children }) => {
  const audioRef = useRef(null); // novo_pedido
  const chamadaSenhaAudioRef = useRef(null); // chamada_senha — separado de novo_pedido
  const alertAudioRef = useRef(null); // alerta_whatsapp
  const alertTimerRef = useRef(null);
  const [soundUnlocked, setSoundUnlocked] = useState(
    sessionStorage.getItem('sound_unlocked') === '1'
  );

  const [whatsappPendentes, setWhatsappPendentes] = useState(0);

  // NOVO: O contexto agora gerencia o mute de forma isolada
  const [botMuted, setBotMuted] = useState(sessionStorage.getItem('bot_muted') === 'true');

  // Preferências de som por evento (config_sons de tb_empresa). null = ainda
  // não buscou/não tem empresa logada; {} = buscou e empresa nunca configurou
  // nada (usa fallback hardcoded em todos os eventos).
  const [configSons, setConfigSons] = useState(null);

  const location = useLocation();
  const path = location.pathname.toLowerCase();
  const isMutedRoute =
    path.startsWith('/app') ||
    path.includes('/auth/login') ||
    path.includes('/mesa/') ||
    path === '/';

  // Busca a config de sons da empresa. Falha silenciosamente — os sons
  // hardcoded (resolverUrlSom com configEvento ausente) já funcionam antes
  // disso resolver e continuam funcionando se a busca falhar. `aplicar` é
  // usado pelo efeito de mount pra evitar setState após desmontar.
  const carregarConfigSons = (aplicar = (config) => setConfigSons(config)) => {
    const codEmpresa = sessionStorage.getItem('cod_empresa');
    const token = sessionStorage.getItem('token');
    if (!codEmpresa || !token) return Promise.resolve();

    return getConfigSons(codEmpresa, token)
      .then((config) => aplicar(config || {}))
      .catch(() => {});
  };

  // Busca uma vez ao entrar em rota não-mutada. `recarregarSons` (exposto no
  // contexto) permite que a tela de configuração force uma nova busca depois
  // de salvar — sem isso, uma aba já aberta (ex: Painel de Pedidos) continua
  // tocando o som antigo até a página inteira ser recarregada, porque esse
  // efeito só roda de novo se `isMutedRoute` mudar, não a cada navegação.
  useEffect(() => {
    if (isMutedRoute) return;

    let cancelado = false;
    carregarConfigSons((config) => {
      if (!cancelado) setConfigSons(config);
    });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMutedRoute]);

  useEffect(() => {
    if (isMutedRoute) return;

    const audio = new Audio(resolverUrlSom('novo_pedido', configSons?.novo_pedido));
    audio.preload = 'auto';
    audio.volume = 0.8;
    audioRef.current = audio;

    const chamadaSenhaAudio = new Audio(resolverUrlSom('chamada_senha', configSons?.chamada_senha));
    chamadaSenhaAudio.preload = 'auto';
    chamadaSenhaAudio.volume = 0.8;
    chamadaSenhaAudioRef.current = chamadaSenhaAudio;

    const alertAudio = new Audio(resolverUrlSom('alerta_whatsapp', configSons?.alerta_whatsapp));
    alertAudio.preload = 'auto';
    alertAudio.volume = 0.6;
    alertAudioRef.current = alertAudio;
  }, [isMutedRoute, configSons]);

  useEffect(() => {
    if (isMutedRoute) return;
    if (sessionStorage.getItem('sound_unlocked') === '1') {
      setSoundUnlocked(true);
    }
  }, [isMutedRoute]);

  useEffect(() => {
    if (isMutedRoute || soundUnlocked || !audioRef.current) return;

    const unlock = () => {
      const audiosToUnlock = [audioRef.current];
      if (chamadaSenhaAudioRef.current) audiosToUnlock.push(chamadaSenhaAudioRef.current);
      if (alertAudioRef.current) audiosToUnlock.push(alertAudioRef.current);

      Promise.all(
        audiosToUnlock.map((a) => {
          return a
            .play()
            .then(() => {
              a.pause();
              a.currentTime = 0;
            })
            .catch(() => {});
        })
      ).then(() => {
        setSoundUnlocked(true);
        sessionStorage.setItem('sound_unlocked', '1');
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
      });
    };

    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [soundUnlocked, isMutedRoute]);

  useEffect(() => {
    if (isMutedRoute) return;
    const el = alertAudioRef.current;
    if (!el) return;

    const tick = async () => {
      try {
        const isPedidoToccando =
          audioRef.current && !audioRef.current.paused && audioRef.current.currentTime > 0;
        if (isPedidoToccando) return;

        el.currentTime = 0;
        await el.play();
      } catch (err) {}
    };

    const start = () => {
      if (alertTimerRef.current) return;
      tick();
      alertTimerRef.current = setInterval(tick, 2000);
    };

    const stop = () => {
      if (alertTimerRef.current) {
        clearInterval(alertTimerRef.current);
        alertTimerRef.current = null;
      }
      el.pause();
      el.currentTime = 0;
    };

    // NOVO: O loop do robô agora respeita a variável botMuted, sem tocar na contagem de clientes!
    if (whatsappPendentes > 0 && soundUnlocked && !botMuted) start();
    else stop();

    return stop;
    // Adicionamos botMuted aqui na lista de dependências
  }, [whatsappPendentes, soundUnlocked, isMutedRoute, botMuted]);

  const playSound = () => {
    if (isMutedRoute) return;
    try {
      if (!audioRef.current || !soundUnlocked) return;
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (error) {}
  };

  // Separado de playSound (novo_pedido): mesmo reaproveitando o mesmo
  // arquivo por padrão hoje, os dois eventos são configuráveis
  // independentemente em config_sons — ver
  // docs/ativo/plano-implementacao-sons-customizaveis.txt.
  const playSomChamadaSenha = () => {
    if (isMutedRoute) return;
    try {
      if (!chamadaSenhaAudioRef.current || !soundUnlocked) return;
      chamadaSenhaAudioRef.current.currentTime = 0;
      chamadaSenhaAudioRef.current.play().catch(() => {});
    } catch (error) {}
  };

  const playAlertSound = () => {
    if (isMutedRoute) return;
    try {
      // NOVO: Garante que o toque avulso do robô também obedeça o mute
      if (!alertAudioRef.current || !soundUnlocked || botMuted) return;
      alertAudioRef.current.currentTime = 0;
      alertAudioRef.current.play().catch(() => {});
    } catch (e) {}
  };

  const contextValue = {
    playSound,
    playSomChamadaSenha,
    playAlertSound,
    setWhatsappPendentes,
    soundUnlocked,
    setSoundUnlocked,
    audioRef,
    botMuted, // NOVO: Exporta a variável
    setBotMuted, // NOVO: Exporta a função para o WhatsAppBell alterar
    recarregarSons: () => carregarConfigSons(), // chamado por SonsModal após salvar
  };

  return <AudioContext.Provider value={contextValue}>{children}</AudioContext.Provider>;
};
